import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, getToken } from "./api";
import { setWorkspaceTimezone } from "./datetime";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [workspace, setWorkspace] = useState(null);
    const [memberships, setMemberships] = useState([]);
    const [loading, setLoading] = useState(true);

    // Keep the global date/time formatter in sync with the workspace timezone
    useEffect(() => { setWorkspaceTimezone(workspace?.timezone); }, [workspace?.timezone]);

    const _apply = (data) => {
        if (data.token) setToken(data.token);
        if (data.user) setUser(data.user);
        if (data.workspace) setWorkspace(data.workspace);
        if (Array.isArray(data.memberships)) setMemberships(data.memberships);
    };

    const bootstrap = useCallback(async () => {
        const token = getToken();
        if (!token) { setLoading(false); return; }
        try {
            const res = await api.get("/auth/me");
            _apply(res.data);
        } catch (e) {
            setToken(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { bootstrap(); }, [bootstrap]);

    const login = async (email, password) => {
        const res = await api.post("/auth/login", { email, password });
        _apply(res.data);
        return res.data;
    };

    const register = async (payload) => {
        const res = await api.post("/auth/register", payload);
        _apply(res.data);
        return res.data;
    };

    const addWorkspace = async ({ email, password, workspace_name, tier = "solo" }) => {
        const res = await api.post("/auth/add-workspace", { email, password, workspace_name, tier });
        _apply(res.data);
        return res.data;
    };

    const switchWorkspace = async (workspace_id) => {
        const res = await api.post("/auth/switch", { workspace_id });
        _apply(res.data);
        return res.data;
    };

    const logout = () => {
        setToken(null);
        setUser(null);
        setWorkspace(null);
        setMemberships([]);
    };

    const refreshWorkspace = async () => {
        try {
            const res = await api.get("/auth/me");
            _apply(res.data);
        } catch (e) { /* ignore */ }
    };

    return (
        <AuthCtx.Provider value={{
            user, workspace, memberships, loading,
            login, register, addWorkspace, switchWorkspace, logout, refreshWorkspace,
        }}>
            {children}
        </AuthCtx.Provider>
    );
}

export function useAuth() {
    return useContext(AuthCtx);
}
